import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { useParams } from 'react-router';
import { DateTimePicker } from '@material-ui/pickers';
import {
  EventDTO,
  GeneralEvent,
  SportNameShort,
  TEAM_SPECIFIC_EVENTS,
  eventsSportMap,
} from 'screens/liveScoring/DTO';
import { stores } from 'mobx-store';
import { Stack } from 'immutable';

interface AddEditEventModalProps {
  open: boolean;
  onClose: () => void;
  event: EventDTO | null;
}

const AddEditEventModal: React.FC<AddEditEventModalProps> = observer(
  ({ open, onClose, event }) => {
    const { liveScoringStore } = stores;
    const { id: matcheId } = useParams<{ id: string }>();
    const match = liveScoringStore.match;

    // Form validation
    const validationSchema = Yup.object().shape({
      type: Yup.string().required('Type is required'),
      eventAt: Yup.date().required('Event date is required'),
      teamId: Yup.mixed().test(
        'is-number-or-any',
        'Invalid teamId value',
        (value) => value === null || value === undefined || !isNaN(Number(value))
      ),
      description: Yup.string().required('Description is required'),

      notification: Yup.boolean().required().default(false),

    });

    const handleSubmit = async (values: any) => {
      try {
        const { type, description, eventAt, teamId, notification } = values;

        if (event?.id) {
          // Update existing event
          await liveScoringStore.updateEvent(event.id, {
            type,
            description,
            eventAt,
            teamId,
          });
        } else {
          // Create new event + notification
          await liveScoringStore.createEvent(
            {
              event: {
                type,
                description,
                eventAt,
                matchId: Number(matcheId),
                teamId: teamId || null,
              },
              notification,
            }
          );
        }
        onClose();
      } catch (err) {
        console.error('Error saving event:', err);
      }
    };

    const nameShort = match?.tournament?.sport?.nameShort as SportNameShort;

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{event ? 'Edit Event' : 'Add New Event'}</DialogTitle>
        <Formik
          initialValues={{
            id: event?.id ?? 0,
            type: event?.type ?? '',
            description: event?.description ?? '',
            eventAt: event?.eventAt ? new Date(event.eventAt) : new Date(),
            teamId: event?.teamId ?? null,
            notification: false,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogContent>
                {/* Event Type */}
                <Field
                  as={TextField}
                  select
                  name="type"
                  label="Event Type"
                  fullWidth
                  margin="normal"
                  error={touched.type && !!errors.type}
                  helperText={touched.type && errors.type}
                >
                  {Object.values(eventsSportMap[nameShort] || {}).map((eventType) => (
                    <MenuItem key={eventType} value={eventType}>
                      {eventType.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Field>

                {/* Team Selector */}
                {TEAM_SPECIFIC_EVENTS.includes(values.type as any) && (
                  <Field
                    as={TextField}
                    select
                    name="teamId"
                    label="Team"
                    fullWidth
                    margin="normal"
                    error={touched.teamId && !!errors.teamId}
                    helperText={touched.teamId && errors.teamId}
                  >
                    {[match?.teamH, match?.teamA].map(
                      (team) =>
                        team && (
                          <MenuItem key={team.id} value={team.id}>
                            {team.name}
                          </MenuItem>
                        )
                    )}
                  </Field>
                )}

                {/* Event Date */}
                <DateTimePicker
                  label="Event Date and Time"
                  value={values.eventAt}
                  onChange={(newValue) => newValue && setFieldValue('eventAt', newValue)}
                  fullWidth
                  margin="normal"
                />

                {/* Event Description */}
                <Field
                  as={TextField}
                  name="description"
                  label="Event Description"
                  fullWidth
                  margin="normal"
                  error={touched.description && !!errors.description}
                  helperText={touched.description && errors.description}
                />

                {/* Notification Fields (only when creating) */}
                {!event && (
                  <>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          name="notification"
                          color="primary"
                          type="checkbox"
                        />
  }
                      label="Send Notification"
                    />

                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} color="secondary">
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
);

export default AddEditEventModal;
